<template>
  <v-layout>
    <section class="repos" :class="{repos_loading: gettingRepos}">
      <h1 class="intro">
        {{ $t('PUBLIC_ALTSTORE_REPOS') }}
      </h1>
      <div v-if="gettingRepos" class="loader-wrapper">
        <loader />
      </div>
      <template v-else>
        <div class="items-wrapper">
          <p class="instruction" v-if="repos.length">
            {{ $t("MY_REPOS_MOBILE_INSTRUCTION") }}
          </p>
          <div class="items">
            <div v-if="!repos.length">
              No public repositories found
            </div>
            <div
              class="item item_active"
              v-for="repo in repos"
              :class="{
                item_disabled: repoRemovingId === repo.id,
              }"
              :key="repo.id"
              @click="browseRepoMobile(repo.id, $event)"
            >
              <div class="item__desc">
                <span class="item__title">
                  {{ repo.name }}
                </span>
                <span class="item__info">
                  {{ $t('STATUS_IS_X', {status: repo.status_translated}) }}
                  <br/>
                  {{ $t('APPS_N', {apps: repo.total_apps}) }}
                  <br/>
                  {{ $t('LAST_CHECKED_AT',
                    {date: new Date(repo.last_checked_at * 1000).toLocaleString()}) }}
                </span>
              </div>
              <div class="item__switch">
                <router-link class="item__button" :to="`/repos/${repo.id}`">
                  {{ $t("BROWSE") }}
                </router-link>
              </div>
            </div>
          </div>
        </div>
        <div class="link">
          <html-with-links
            tag="p"
            class="link__text"
            :html="$t('SEARCHING_FOR_PRIVATE_REPOS', {href: '/my/repos'})"
          />
          <p class="link__text">
            {{ $t("MY_ALTSTORE_REPOSITORIES_SIDEBAR") }}
          </p>
        </div>
      </template>
    </section>
  </v-layout>
</template>

<script>
import { mapGetters } from 'vuex';
import isItselfLinkable from '@/utils/isItselfLinkable';
import Loader from '@/components/Loader.vue';
import VLayout from '@/views/My/index.vue';
import HtmlWithLinks from '@/components/HtmlWithLinks';

export default {
  components: {
    Loader, VLayout, HtmlWithLinks,
  },
  data() {
    return {
      origin: window.location.origin,
      code: '',
      isMobile: isItselfLinkable(),
      deviceRemoving: null,
      repoRemovingId: null,
      gettingRepos: true,
      repos: [],
    };
  },
  computed: {
    title() {
      return this.$t('PUBLIC_ALTSTORE_REPOS');
    },
    ...mapGetters(['isDeviceLinked']),
  },
  methods: {
    tryAddRepository() {
      if (!this.isDeviceLinked) {
        this.$confirm(this.$t('LINK_TO_ADD_REPO')).then(() => {
          this.$router.push('/link');
        });
        return;
      }

      this.$prompt(this.$t('ENTER_REPOSITORY_URL'), '', { validate: this.validateRepoURL })
        .then((url) => {
          this.$router.push(`/repos/import?url=${encodeURIComponent(url)}`);
        });
    },
    validateRepoURL(url) {
      try {
        // eslint-disable-next-line no-new
        new URL(url);

        return true;
      } catch {
        this.$notify({
          type: 'error',
          text: this.$t('PROVIDE_VALID_URL'),
        });

        return false;
      }
    },
    browseRepo(id) {
      this.$router.push(`/repos/${id}`);
    },
    removeRepo(id) {
      this.repoRemovingId = id;
      this.$api.deleteAltstoreRepo(id)
        .then(() => {
          const idx = this.repos.findIndex(repo => repo.id === id);
          this.repos.splice(idx, 1);
        })
        .finally(() => {
          this.repoRemovingId = null;
        });
    },
    browseRepoMobile(id) {
      if (window.innerWidth >= 1250) {
        return;
      }
      this.$router.push(`/repos/${id}`);
    },
    getRepos() {
      this.gettingRepos = true;
      return this.$api.getAltstoreRepos(1)
        .then((response) => {
          this.repos = response;
        })
        .finally(() => {
          this.gettingRepos = false;
        });
    },
  },
  created() {
    this.getRepos();
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/variables";

.repos {
  flex-grow: 1;
  display: grid;
  align-items: start;
  grid-template-columns: 1fr 200px;
  column-gap: 50px;
  row-gap: 25px;
  grid-template-rows: min-content;
  grid-template-areas:
    'header header'
    'content sidebar'
  ;

  &_loading {
    grid-template-areas:
      'header header'
      'loader loader';
  }

  @include notebook {
    grid-template-columns: 1fr;
    grid-template-areas: 'header' 'sidebar' 'loader' 'content';
  }
}

.loader-wrapper {
  grid-area: loader;
}

.main .intro {
  grid-area: header;
  margin-bottom: 0;
}

.instruction {
  display: none;

  @include notebook {
    display: block;
    font-size: 12px;
    margin-bottom: 15px;
    padding: 0 10px;
  }
}

.items-wrapper {
  grid-area: 'content';
}

.items {
  flex-grow: 1;
}

.item {
  display: flex;
  padding: 10px;
  align-items: center;
  border-radius: 4px;
  margin-bottom: 15px;
  @include transition;

  &:not(:first-child) {
    margin-top: 5px;
  }

  &_active {
    box-shadow: var(--my-device-shadow);
    border-color: transparent;
  }

  &_disabled {
    pointer-events: none;
  }

  &__img-wrapper {
    flex: 0 0 44px;
    text-align: center;
    margin-right: 15px;
  }

  &__img {
    height: 45px;
    filter: var(--img-filter);
  }

  &__title {
    font-weight: 500;
    display: flex;
    align-items: center;
    margin-bottom: 5px;
  }

  &__pro {
    text-transform: uppercase;
    background: $primary;
    font-size: 11px;
    font-weight: 700;
    color: #fff;
    padding: 3px 3px 2px;
    margin-left: 5px;
  }

  &__remove {
    color: #fa6565;
    margin-left: 5px;
  }

  &__desc {
    flex-grow: 1;
  }

  &__info {
    line-height: 21px;
  }

  &__jail {
    white-space: nowrap;
    border-radius: 2.5px;
    font-size: 12px;
    padding: 1px 3px 2px;
    line-height: 10px;
    position: relative;
    top: -1px;
    margin-left: 2px;
    background: var(--tertiary-accent);
    color: var(--text-tertiary-accent);

    &_active {
      background: var(--bg-success);
      color: #053305;
    }
  }

  &__switch {
    text-align: center;

    @include notebook {
      display: none;
    }
  }

  &__button {
    background: var(--bg);
    border: 1px solid lighten($primary, 20%);
    border-radius: 2px;
    color: var(--text-primary);
    font-size: 12px;
    padding: 3px 15px;
  }

  &__switch-text {
    color: $text-secondary;
    font-size: 12px;
    margin-top: 2px;
  }
}

.link {
  width: 200px;
  background: var(--secondary-bg);
  border-radius: 8px;
  padding: 10px;
  grid-area: sidebar;

  @include notebook {
    justify-self: center;
    width: 100%;
    max-width: 400px;
  }

  &__title {
    font-weight: 500;
    margin-bottom: 10px;
    text-align: left;
  }

  &__text {
    line-height: 15px;
    text-align: left;
    margin-bottom: 15px;
    word-break: break-word;

    &:last-child {
      margin-bottom: 0;
      margin-top: 15px;
    }

    &::v-deep {
      b {
        word-break: break-all;
        font-weight: 700;
      }
    }
  }

  &__code {
    display: block;
    font-size: 20px;
    font-weight: 600;
    letter-spacing: 5px;
    margin: 10px 0 5px;
  }

  &__qr {
    margin-top: 10px;
    margin-bottom: 15px;
  }

  &__list {
    list-style: decimal inside;
    text-align: left;

    &::v-deep {
      li {
        margin-bottom: 3px;
        line-height: 18px;
      }

      strong {
        font-weight: bold;
      }
    }
  }
}
</style>
